import { useMemo, useState } from 'react';

import { isStoryNpc } from '@/roster/persona';
import { initials } from '@/lib/initials';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, ScrollText, Swords, X } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { setActiveSession, setBrowsingIdentity, startSession } from '@/store/gameSlice';
import { writeTabIdentity } from '@/store/browsingIdentity';
import { useSelectCharacterMutation } from '@/roster/queries';
import { useLogout } from '@/evennia_replacements/queries';
import { useGameSocket } from '@/hooks/useGameSocket';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import type { MyRosterEntry } from '@/roster/types';
import { useDisconnectCharacter } from '@/roster/useDisconnectCharacter';
import { WeatherWidget } from '@/weather/components/WeatherWidget';
import { ComfortWidget } from '@/comfort/components/ComfortWidget';
import { characterAttention } from '@/game/attention';
import { wakingKinds } from '@/game/feedChips';
import { usePlayPreferences } from '@/game/playPreferences';
import { AttentionBadge } from '@/game/components/AttentionBadge';
// #3412 S4 — reused from the Hall (frontend/src/home/hall/queries.ts), not
// duplicated: no import-boundary lint rule exists between home/ and game/
// (checked eslint.config.js — no `boundaries`/`no-restricted-imports` rule
// is configured at all), so the smallest change is importing directly
// rather than relocating the hook.
import { useClockQuery } from '@/home/hall/queries';

import { FormSwitcher } from './FormSwitcher';
import { PersonaSwitcher } from './PersonaSwitcher';

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

function capitalize(s: string): string {
  return s.length === 0 ? s : s[0].toUpperCase() + s.slice(1);
}

/**
 * Compact IC-season readout (#3412 S4) beside the WeatherWidget. Deliberately
 * NOT hh:mm — WeatherWidget already surfaces `phase + hh:mm` from the same
 * game_clock backend (`get_ic_now`) via its own `ic_time` field, so a second
 * clock readout duplicating that number would just be visual noise (review
 * finding, folded in-branch rather than filed). ClockReadout shows what
 * WeatherWidget lacks — season, plus the paused indicator — and keeps the
 * full date/time/phase in the title tooltip. Mirrors WeatherWidget's own
 * hide-until-resolved shape: renders nothing while loading or on error (no
 * `throwOnError` on `useClockQuery` — an errored fetch just resolves
 * `data: undefined`), so there's no layout jump.
 */
function ClockReadout() {
  const { data: clock } = useClockQuery();
  if (!clock) return null;

  const seasonLabel = capitalize(clock.season);
  const tooltip = [
    `${clock.date_display}, ${pad(clock.hour)}:${pad(clock.minute)}`,
    capitalize(clock.phase),
  ]
    .filter(Boolean)
    .join(' — ');

  return (
    <div
      className="flex items-center gap-1 text-xs text-muted-foreground"
      title={tooltip}
      aria-label="The world clock"
    >
      <span>{seasonLabel}</span>
      {/* PLACEHOLDER copy */}
      {clock.paused && <span className="text-muted-foreground/70">(Paused)</span>}
    </div>
  );
}

interface GameTopBarProps {
  characters: MyRosterEntry[];
  /** Drives the dismissable combat banner (#3761). */
  hasActiveEncounter?: boolean;
  encounterId?: number;
  onJumpToCombat?: () => void;
}

/** NPC or CROWD mark for a character the account plays but does not own (#4418). */
function NpcTag({ entry }: { entry: MyRosterEntry }) {
  if (!isStoryNpc(entry)) return null;
  return (
    <span
      data-testid="npc-tag"
      className="rounded border px-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground"
    >
      {entry.is_crowd_voice ? 'CROWD' : 'NPC'}
    </span>
  );
}

/** Where a puppeted character stands, under its name on the tab (demo Screen 3). */
function RoomCaption({ entry }: { entry: MyRosterEntry }) {
  if (!entry.room_name) return null;
  return (
    <span
      data-testid="tab-room"
      className="block max-w-28 truncate text-[10px] leading-tight text-muted-foreground"
    >
      {entry.room_name}
    </span>
  );
}

/**
 * Urgent, dismissable "in combat" strip (#3761) — full-width, rendered below
 * the main bar so it doesn't compete for space in the already-crowded
 * flex row. Keyed by `encounterId` at the call site so a new encounter
 * always starts un-dismissed, without this component needing its own
 * encounter-change effect.
 */
function CombatBanner({ onJumpToCombat }: { onJumpToCombat?: () => void }) {
  const [dismissed, setDismissed] = useState(false);
  if (dismissed) return null;
  return (
    <div
      data-testid="combat-banner"
      className="flex items-center gap-2 bg-destructive px-3 py-1.5 text-xs font-semibold text-destructive-foreground sm:px-4"
    >
      <Swords className="h-3.5 w-3.5" />
      <button
        type="button"
        onClick={onJumpToCombat}
        className="min-h-6 text-left underline-offset-2 hover:underline"
      >
        In combat
      </button>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={() => setDismissed(true)}
        className="ml-auto flex min-h-6 min-w-6 items-center justify-center rounded hover:bg-black/10"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

export function GameTopBar({
  characters,
  hasActiveEncounter,
  encounterId,
  onJumpToCombat,
}: GameTopBarProps) {
  const dispatch = useAppDispatch();
  const { connect } = useGameSocket();
  const { sessions, active } = useAppSelector((state) => state.game);
  const activeEntry = characters.find((c) => c.name === active) ?? null;
  const disconnectCharacter = useDisconnectCharacter(activeEntry);
  // The chips decide what wakes (#3856): a kind under a chip that is off or
  // silent never badges an avatar here either.
  const accountId = useAppSelector((state) => state.auth.account?.id ?? null);
  const { preferences } = usePlayPreferences(accountId);
  const waking = useMemo(() => wakingKinds(preferences.feedChips), [preferences.feedChips]);
  const selectCharacter = useSelectCharacterMutation();
  const queryClient = useQueryClient();
  const logout = useLogout();
  const navigate = useNavigate();

  // #3818 "Disconnect Character" (renamed from "Leave the world", #4321):
  // drop this character's socket so the server unpuppets them (nobody left
  // standing unpiloted on the grid), keep the account signed in and the
  // selection intact for offscreen play, and go to character select. The
  // account refetch re-hydrates the selection mirror if this was the last
  // open session (the close handler resets game state). "Every connection"
  // (#4321) asks the server to close the character's other sessions too; the
  // close handler then does all of this for this tab when its own socket goes.
  const disconnectThisConnection = () => {
    if (!active) return;
    disconnectCharacter.disconnectThis();
    void queryClient.invalidateQueries({ queryKey: ['account'] });
    navigate('/hall');
  };

  const activeSession = active ? sessions[active] : null;
  const isConnected = activeSession?.isConnected ?? false;
  const isReady = isConnected && Boolean(activeSession?.room);
  let connectionLabel = 'Disconnected';
  let connectionColor = 'bg-red-500';
  if (isConnected) {
    connectionLabel = isReady ? 'In world' : 'Waiting for location';
    connectionColor = isReady ? 'bg-green-500' : 'bg-amber-500';
  }

  const handleSelectCharacter = async (name: MyRosterEntry['name']) => {
    // #3479 decision 4: an in-game switch is tab-local. It writes THIS tab's
    // browsing identity (sessionStorage + the gameSlice mirror) so the
    // ambient pages this tab opens follow the character it now plays, and
    // no other tab is touched: every tab seeds its identity from its own
    // sessionStorage, never from the account refetch (store/browsingIdentity.ts).
    //
    // The durable account selection is still written, but only when this
    // switch takes a character UP (opens a socket), and BEFORE the connect:
    // login puppets the server's durable selection the moment the socket
    // authenticates (#3812, ADR-0294), so the select has to land first or the
    // new socket briefly puppets the previous character before its own `@ic`
    // corrects it. Puppeting records the selection server-side anyway
    // (ADR-0294), so this is the same fact landed a moment earlier, not a
    // second writer; moving focus between two already-open sessions writes
    // nothing durable. A failed select is not blocking: the socket still
    // opens and its `@ic <name>` still names the character (see
    // useSelectCharacterMutation's doc comment on the degraded case).
    const entryId = characters.find((c) => c.name === name)?.id;
    const alreadyConnected = Boolean(sessions[name]?.isConnected);
    if (entryId !== undefined) {
      writeTabIdentity(entryId);
      dispatch(setBrowsingIdentity(entryId));
    }
    if (sessions[name]) {
      dispatch(setActiveSession(name));
    } else {
      dispatch(startSession(name));
    }
    if (!alreadyConnected) {
      if (entryId !== undefined) {
        try {
          await selectCharacter.mutateAsync(entryId);
        } catch {
          // onError already toasted; the connect below still carries the intent.
        }
      }
      connect(name);
    }
    // #3774 -- switching is the moment the player expects the badge they just
    // acted on to be right.
    void queryClient.invalidateQueries({ queryKey: ['my-roster-entries'] });
  };

  const activeCharacter = characters.find((c) => c.name === active);
  const otherCharacters = characters.filter((c) => c.name !== active);

  return (
    <>
      <div className="flex min-w-0 flex-wrap items-center gap-2 border-b bg-card px-3 py-2 sm:gap-4 sm:px-4">
        {/* #3818 — a real menu. This was `<Link to="/">`, and `/` sends an
            in-world player straight back to `/game` (GatefoldPage), so it
            flickered and did nothing, and nothing led back to character select.
            Sessions and sockets live in Redux/module scope, so every item here
            keeps the open character tabs alive. */}
        <DropdownMenu>
          <DropdownMenuTrigger
            aria-label="Open world menu"
            title="World menu"
            className="inline-flex min-h-11 min-w-11 items-center justify-center rounded p-2 hover:bg-accent focus:outline-none"
          >
            <Menu className="h-5 w-5" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuItem asChild>
              <Link to="/hall">Your characters</Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/roster">Roster</Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/profile/settings">Settings</Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {active && sessions[active] && disconnectCharacter.needsChoice && (
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>Disconnect Character</DropdownMenuSubTrigger>
                <DropdownMenuSubContent>
                  <DropdownMenuItem onClick={disconnectThisConnection}>
                    This connection
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={disconnectCharacter.disconnectEverywhere}>
                    Every connection
                  </DropdownMenuItem>
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            )}
            {active && sessions[active] && !disconnectCharacter.needsChoice && (
              <DropdownMenuItem onClick={disconnectThisConnection}>
                Disconnect Character
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={() => logout.mutate()}>Log Out Account</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <span className="text-sm font-bold tracking-wide text-foreground">ARX II</span>

        <div className="mx-2 h-6 w-px bg-border" />

        {characters.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No characters yet -{' '}
            <Link to="/roster" className="text-primary underline">
              browse the roster
            </Link>{' '}
            or{' '}
            <Link to="/characters/create" className="text-primary underline">
              create one
            </Link>
            .
          </p>
        )}

        {active && activeCharacter ? (
          <div className="flex items-center gap-3">
            {/* #3412 — a hydrated-on-reload selection has no live session yet
              (selection isn't presence, so hydration never auto-connects).
              Clickable so there's still a way to (re)connect; harmless
              no-op when already connected (handleSelectCharacter just
              re-activates the existing session). */}
            <button
              onClick={() => handleSelectCharacter(active)}
              title={isConnected ? active : `Connect as ${active}`}
            >
              <Avatar className="h-9 w-9 ring-2 ring-primary">
                <AvatarImage src={activeCharacter.profile_picture_url ?? undefined} alt={active} />
                <AvatarFallback className="text-xs">{initials(active)}</AvatarFallback>
              </Avatar>
            </button>
            <NpcTag entry={activeCharacter} />
            <RoomCaption entry={activeCharacter} />
            <PersonaSwitcher
              characterSheetId={activeCharacter.character_id}
              activePersonaId={activeCharacter.active_persona_id}
            />
            <FormSwitcher characterSheetId={activeCharacter.character_id} />
            {/* #3412 S4 — own-sheet link, mode-preserving (new tab so leaving
              the game window doesn't drop the WebSocket session). Route
              param is the RosterEntry id (App.tsx: /characters/:id ->
              CharacterSheetPage reads useParams().id as entryId), not
              character_id. */}
            <Link
              to={`/characters/${activeCharacter.id}`}
              target="_blank"
              rel="noopener"
              className="text-muted-foreground transition-colors hover:text-foreground"
              title="Your character sheet" // PLACEHOLDER copy
              aria-label="Your character sheet" // PLACEHOLDER copy
            >
              <ScrollText className="h-4 w-4" />
            </Link>
          </div>
        ) : null}

        {active &&
          otherCharacters.map((char) => {
            const session = sessions[char.name];
            const attention = characterAttention(char, session, {
              wakingKinds: waking,
              dismissed: new Set(session?.dismissedFeed ?? []),
            });
            // Ruling B (#3774 demo) -- a character with nothing waiting and no
            // local session stays in the dimmest tier; anything waiting, or a
            // live session, steps it up. A badge on a 40%-opacity avatar reads
            // as decoration, so unread attention alone is enough to promote it.
            const isDim = !session && attention.direct === 0 && !attention.ambient;
            return (
              <button
                key={char.id}
                onClick={() => handleSelectCharacter(char.name)}
                className={
                  isDim
                    ? 'relative opacity-40 transition-opacity hover:opacity-80'
                    : 'relative opacity-60 transition-opacity hover:opacity-100'
                }
                // Ruling A (#3774 review) -- the two lists this replaces had
                // different titles for a real reason: one switches to an
                // already-connected session, the other opens a new connection.
                title={session ? `Switch to ${char.name}` : `Connect as ${char.name}`}
              >
                <Avatar className={isDim ? 'h-6 w-6' : 'h-7 w-7'}>
                  <AvatarImage src={char.profile_picture_url ?? undefined} alt={char.name} />
                  <AvatarFallback className={isDim ? 'text-[10px]' : 'text-xs'}>
                    {initials(char.name)}
                  </AvatarFallback>
                </Avatar>
                <AttentionBadge direct={attention.direct} ambient={attention.ambient} />
                <NpcTag entry={char} />
                <RoomCaption entry={char} />
              </button>
            );
          })}

        {!active &&
          characters.map((char) => (
            <button
              key={char.id}
              onClick={() => handleSelectCharacter(char.name)}
              className="flex items-center gap-2 rounded px-2 py-1 text-sm hover:bg-accent"
            >
              <Avatar className="h-7 w-7">
                <AvatarImage src={char.profile_picture_url ?? undefined} alt={char.name} />
                <AvatarFallback className="text-xs">{initials(char.name)}</AvatarFallback>
              </Avatar>
              <span>{char.name}</span>
              <NpcTag entry={char} />
            </button>
          ))}

        <div className="ml-auto flex min-w-0 items-center gap-2 sm:gap-3">
          <ComfortWidget characterId={activeCharacter?.character_id ?? null} />
          <ClockReadout />
          <WeatherWidget />
          <div className="flex items-center gap-2">
            <div className={`h-2 w-2 rounded-full ${connectionColor}`} />
            <span className="hidden text-xs text-muted-foreground sm:inline">
              {connectionLabel}
            </span>
          </div>
        </div>
      </div>
      {hasActiveEncounter && <CombatBanner key={encounterId} onJumpToCombat={onJumpToCombat} />}
    </>
  );
}
